import { Injectable } from "@angular/core";
import { ExcelDataMapperService } from "./excel-data-mapper.service";
import { ApplicationContext, ReturnCalendarData, SourceSheet, TableConfig } from "src/app/model";
import { HttpService } from "../http-service/http.service";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { API_ENDPOINTS } from "src/app/config/api-endpoints";
import { ApplicationInsightsService } from "../application-insights/application-insights.service";
import { WOKRPAPER_SHEETS } from "src/app/constants/workbook.constants";
import { SaveHeadersProvider } from "./save-headers-provider.interface";

@Injectable({
  providedIn: "root",
})
export class ReturnsCalendarDataService implements SaveHeadersProvider {
  private isConfigLoaded = false;
  private config: TableConfig = {
    sheetType: SourceSheet.ReturnsCalendar,
    columnMappings: [],
  };
  private readonly columnMappings = [
    { Key: "EntityName", DisplayName: "Entity", Column: "A" },
    { Key: "VertexCompanyCode", DisplayName: "Vertex Company Code", Column: "B" },
    { Key: "ReturnName", DisplayName: "Return Name", Column: "C" },
    { Key: "LocationCode", DisplayName: "Location Code", Column: "D" },
    { Key: "LocalCode", DisplayName: "Local Code", Column: "E" },
    { Key: "RegistrationNumber", DisplayName: "Registration Number", Column: "F" },
    { Key: "Frequency", DisplayName: "Frequency Locked", Column: "G" },
    { Key: "DueDay", DisplayName: "Due Day from Master", Column: "H" },
    { Key: "FileMethod", DisplayName: "File Method", Column: "I" },
    { Key: "PaymentMethod", DisplayName: "Payment Method", Column: "J" },
    { Key: "NonVertexReturn", DisplayName: "Non-Vetex Return", Column: "K" },
    { Key: "EdiUploadAvailable", DisplayName: "Edi Upload Available", Column: "L" },
    { Key: "TaxSchema", DisplayName: "TaxSchema", Column: "M" },
    { Key: "TaxPayerVerification", DisplayName: "AbTaxPayerVerification", Column: "N" },
    { Key: "TaxType", DisplayName: "AbTaxType", Column: "O" },
    { Key: "TaxCode", DisplayName: "AbTaxCode1", Column: "P" },
    { Key: "MasterReturnId", DisplayName: "MasterReturnId", Column: "Q" },
    { Key: "EntityMasterReturnId", DisplayName: "EntityMasterReturnId", Column: "R" },
  ];
  constructor(
    private excelDataMapperService: ExcelDataMapperService,
    private http: HttpService,
    private appInsights: ApplicationInsightsService
  ) {
    this.loadConfig();
  }

  public getSaveHeaders(): string[] {
    return Object.keys(this.mapReturnCalendar);
  }
  private loadConfig() {
    this.appInsights.trackEvent("ReturnsCalendarDataService.loadConfig.called");
    try {
      if (!this.isConfigLoaded) {
        this.appInsights.trackEvent("ReturnsCalendarDataService.loadConfig.settingSheetName", { sheetName: "Returns Calendar" });
        this.appInsights.trackEvent("ReturnsCalendarDataService.loadConfig.settingTableName", { tableName: "ReturnCalendarData" });
        this.isConfigLoaded = true;
        this.appInsights.trackEvent("ReturnsCalendarDataService.loadConfig.configLoaded");
      }
    } catch (error: any) {
      this.appInsights.trackException(error, { location: "ReturnsCalendarDataService.loadConfig" });
      this.appInsights.trackEvent("ReturnsCalendarDataService.loadConfig.error", { error: error?.message || error });
      console.error("Error loading config in ReturnsCalendarDataService:", error);
    }
  }
  public getConfig(appContext?: ApplicationContext): TableConfig {
    this.loadConfig();
    this.config.columnMappings = [...this.columnMappings];
    return this.config;
  }
  public getData(
    appContext: ApplicationContext
  ): Observable<ReturnCalendarData[]> {
    const isRG = !!appContext.reportingGroupId;
    const endpoint = isRG
      ? API_ENDPOINTS.returnCalendar.getReturnsForRG
      : API_ENDPOINTS.returnCalendar.getReturns;
    const url = `${environment.apiBaseURL}${endpoint}`;
    return this.http.post(url, appContext);
  }
  public prepareReturnCalendarSheetData(matrix: string[][]): {
    headers: string[];
    rows: (string | number | boolean)[][];
  } {
    const { sqlHeaders, tableRows } =
      this.excelDataMapperService.buildFilteredData(
        matrix,
        this.mapReturnCalendar,
        WOKRPAPER_SHEETS.ReturnsCalendarSheet,
        {
          skipRows: 0,
          removeLastRow: true,
          skipRowsWhenAllMappedColumnsBlank: true,
        }
      );

    tableRows.unshift(sqlHeaders);

    return { headers: sqlHeaders, rows: tableRows };
  }

  private mapReturnCalendar: Record<string, { sqlCol: string; dataType: string; required: boolean; validate?: (value: any) => boolean }> = {
    'Return Name': { sqlCol: 'ReturnName', dataType: 'string', required: true },
    'EntityMasterReturnId': { sqlCol: 'EntityMasterReturnId', dataType: 'number', required: true },
    'Return State': { sqlCol: 'ReturnState', dataType: 'string', required: true },
    'Vertex Company Code': { sqlCol: 'VertexCompanyCode', dataType: 'string', required: true },
    'Frequency': { sqlCol: 'Frequency', dataType: 'string', required: true },
    'File Method': { sqlCol: 'FileMethod', dataType: 'string', required: false },
    'Payment Method': { sqlCol: 'PaymentMethod', dataType: 'string', required: false },
    'Registration Number': { sqlCol: 'RegistrationNumber', dataType: 'string', required: false },
    'Sales & Use': { sqlCol: 'SalesAndUse', dataType: 'number', required: false },
    'Sales Tax': { sqlCol: 'SalesTax', dataType: 'number', required: false },
    'Use Tax': { sqlCol: 'UseTax', dataType: 'number', required: false },
    'Discounts': { sqlCol: 'Discounts', dataType: 'number', required: false },
    'Excise & B&O Tax': { sqlCol: 'ExciseAndBOTax', dataType: 'number', required: false },
    'Prepayment': { sqlCol: 'Prepayment', dataType: 'number', required: false },
    'Prepay Credit': { sqlCol: 'PrepayCredit', dataType: 'number', required: false },
    'Sales Tax Current Month Carry Forward': { sqlCol: 'SalesTaxCarryForward', dataType: 'number', required: false },
    'Use Tax Current Month Carry Forward': { sqlCol: 'UseTaxCarryForward', dataType: 'number', required: false },
    'Current Month Carry Forward': { sqlCol: 'CurrentMonthCarryForward', dataType: 'number', required: false },
    'Estimated Tax Due': { sqlCol: 'EstimatedTaxDue', dataType: 'number', required: false },
    'Misc. Fee Notes': { sqlCol: 'MiscFeeNotes', dataType: 'string', required: false },
    'Anybill Mailing (x)': { sqlCol: 'AnybillMailing', dataType: 'string', required: false },
    'Preparation Complete': { sqlCol: 'PreparationComplete', dataType: 'string', required: false },
    'Preparation Complete (Date and Time)': { sqlCol: 'PreparationCompleteDate', dataType: 'datetime', required: false },
    'Review Complete': { sqlCol: 'ReviewComplete', dataType: 'string', required: false },
    'Review Complete  (Date and Time)': { sqlCol: 'ReviewCompleteDate', dataType: 'datetime', required: false },
    'EDI Upload Available \n(x)': { sqlCol: 'EDIUploadAvailable', dataType: 'string', required: false },
    'Efile Tracker': { sqlCol: 'EfileTracker', dataType: 'string', required: false },
    'E-File Confirmation Number': { sqlCol: 'EFileConfirmationNumber', dataType: 'string', required: false },
    'E-Payment Confirmation Number': { sqlCol: 'EPaymentConfirmationNumber', dataType: 'string', required: false },
    'Actual Tax Paid': { sqlCol: 'ActualTaxPaid', dataType: 'number', required: false },
    'Difference of Actual to Estimates': { sqlCol: 'ActualVsEstimateDifference', dataType: 'number', required: false },
    'Variance Explanation': { sqlCol: 'VarianceExplanation', dataType: 'number', required: false },
    'E-Filing Submitted': { sqlCol: 'EFilingSubmitted', dataType: 'string', required: false },
    'E-Filing Submitted (Date and Time)': { sqlCol: 'EFilingSubmittedDate', dataType: 'datetime', required: false },
    'Final Confirmation Complete': { sqlCol: 'EPaymentSubmitted', dataType: 'string', required: false },
    'Final Confirmation Complete (Date and Time)': { sqlCol: 'EPaymentSubmittedDate', dataType: 'datetime', required: false },
    'Client Approval': { sqlCol: 'ClientApproval', dataType: 'string', required: false },
    'Client Approval (Date and Time)': { sqlCol: 'ClientApprovalDate', dataType: 'datetime', required: false },
    'Signer Review Complete': { sqlCol: 'SignerReviewComplete', dataType: 'string', required: false },
    'Signer Review Complete (Date and Time)': { sqlCol: 'SignerReviewCompleteDate', dataType: 'datetime', required: false },
    'Due Date': { 
      sqlCol: 'ReturnDueDate', 
      dataType: 'datetime', 
      required: false,
      validate: (value: any) => !isNaN(Date.parse(value)) 
    },
    'Returns Due': { sqlCol: 'IsReturnDue', dataType: 'boolean', required: false },

    'AbTaxPayerVerification': { sqlCol: 'Taxpayerverification', dataType: 'string', required: false },
    'AbTaxType': { sqlCol: 'TaxpaymentCode', dataType: 'string', required: false },
    'AbTaxEndDate': { sqlCol: 'TaxEndDateStr', dataType: 'string', required: false },
    'AbTaxCode1': { sqlCol: 'AccountTypeCode1', dataType: 'string', required: false },
    'AbTaxAmount1': { sqlCol: 'AccountTypeAmount1', dataType: 'string', required: false },
    'AbTaxCode2': { sqlCol: 'AccountTypeCode2', dataType: 'string', required: false },
    'AbTaxAmount2': { sqlCol: 'AccountTypeAmount2', dataType: 'string', required: false },
    'AbTaxCode3': { sqlCol: 'AccountTypeCode3', dataType: 'string', required: false },
    'AbTaxAmount3': { sqlCol: 'AccountTypeAmount3', dataType: 'string', required: false },    
  };
}
